/* 
* Copyright (c) 2026 Send2Me, media capture and delivery platform. All rights reserved. 
* Distributed under the TruVideo proprietary license. 
*/
import React, { useState, useEffect, useMemo } from 'react';
import { Shield, Layers, Save, X, ChevronDown, Check } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { roleService } from '../services/roleService';
import toastService from '../utils/toastService';
import PermissionGrid from '../components/PermissionGrid';
import { useAuth } from '../context/AuthContext';

const EditRole = () => {
  const ROLES_ROUTE = '/roles';
  const ACCOUNTS_CREATE = 'accounts.create';
  const ACCOUNTS_UPDATE = 'accounts.update';
  const ACCOUNTS_DELETE = 'accounts.delete';
  const SCOPE_PLATFORM = 'platform';
  const SCOPE_ACCOUNT = 'account';
  const SCOPE_SUB_ACCOUNT = 'sub_account';

  const { id } = useParams();
  const navigate = useNavigate();
  const { userScope } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [permissions, setPermissions] = useState([]);
  const [loading, setLoading] = useState(true);

  const currentUserScope = userScope || SCOPE_PLATFORM;

  const [form, setForm] = useState({
    name: '',
    displayName: '',
    scope: currentUserScope,
    isDefault: false,
    permissionIds: []
  });

  const [fieldErrors, setFieldErrors] = useState({});
  const [isScopeOpen, setIsScopeOpen] = useState(false);
  const scopeDropdownRef = React.useRef(null);

  const availableScopes = useMemo(() => {
    if (currentUserScope === SCOPE_PLATFORM) { return [SCOPE_PLATFORM, SCOPE_ACCOUNT, SCOPE_SUB_ACCOUNT]; }
    if (currentUserScope === SCOPE_ACCOUNT) { return [SCOPE_ACCOUNT, SCOPE_SUB_ACCOUNT]; }
    return [SCOPE_SUB_ACCOUNT];
  }, [currentUserScope]);

  const filteredPermissions = useMemo(() => {
    const excludedKeys = [];

    if (form.scope === SCOPE_ACCOUNT) {
      excludedKeys.push(ACCOUNTS_CREATE, ACCOUNTS_UPDATE, ACCOUNTS_DELETE);
    } else if (form.scope === SCOPE_SUB_ACCOUNT) {
      excludedKeys.push(
        ACCOUNTS_CREATE, ACCOUNTS_UPDATE, ACCOUNTS_DELETE,
        'sub_accounts.create', 'sub_accounts.update', 'sub_accounts.delete'
      );
    } else {
      // If scope is platform, we don't exclude any permissions since platform includes all scopes
    }

    return permissions.filter(p => !excludedKeys.includes(p.key));
  }, [permissions, form.scope]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const [roleRes, permRes] = await Promise.all([
          roleService.getRoleById(id),
          roleService.getPermissions()
        ]);

        const roleData = roleRes.data?.role || roleRes.data?.data || roleRes.data;
        const permData = permRes.data?.data || permRes.data || [];

        setPermissions(permData);

        // Normalize permission IDs to strings (handles both string and object arrays)
        const normalizedPerms = (roleData.permissionIds || roleData.permissions || []).map(p =>
          typeof p === 'object' ? (p.id || p.key || p._id) : p
        );

        setForm({
          name: roleData.name,
          displayName: roleData.displayName,
          scope: roleData.scope,
          isDefault: roleData.isDefault ?? false,
          permissionIds: normalizedPerms
        });
      } catch (err) {
        toastService.error('Failed to load role details');
        navigate(ROLES_ROUTE);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id, navigate]);

  // Outside click for scope dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (scopeDropdownRef.current && !scopeDropdownRef.current.contains(event.target)) {
        setIsScopeOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const validateForm = () => {
    const errors = {};
    if (!form.name) errors.name = 'Role key is required';
    if (!form.displayName) errors.displayName = 'Display name is required';
    if (form.permissionIds.length === 0) errors.permissionIds = 'Select at least one permission';

    setFieldErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleBlur = (field, value) => {
    if (!value || (typeof value === 'string' && !value.trim())) {
      const fieldLabels = {
        name: 'Role name',
        displayName: 'Display name',
        permissionIds: 'Permissions'
      };
      setFieldErrors(prev => ({ ...prev, [field]: `${fieldLabels[field] || field} is required` }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) { return; }

    try {
      setSubmitting(true);
      // Extract only fields supported by the update API (as per Postman collection)
      const updatePayload = {
        displayName: form.displayName,
        scope: form.scope,
        permissionIds: form.permissionIds
      };
      await roleService.updateRole(id, updatePayload);
      toastService.success('Role updated successfully');
      navigate(ROLES_ROUTE);
    } catch (err) {
      const msg = err.response?.data?.message;
      const errorMsg = Array.isArray(msg) ? msg[0] : (msg || 'Failed to update role');
      toastService.error(errorMsg);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center h-screen space-y-4">
        <div className="w-12 h-12 border-4 border-primary/20 border-t-primary rounded-full animate-spin"></div>
        <p className="text-base-content/40 font-black uppercase tracking-widest text-xs">Synchronizing Role Data...</p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-auto lg:h-[calc(100vh-3rem)] lg:overflow-hidden rounded-2xl border border-base-300 shadow-sm bg-base-100">
      <div className="flex-1 flex flex-col overflow-y-auto bg-base-100/50 backdrop-blur-md">

        {/* Header */}
        <div className="p-6 sm:p-8 border-b border-base-300 flex-shrink-0 flex justify-between items-start">
          <div>
            <h1 className="page-title flex items-center gap-3">
              Update Role
            </h1>
          </div>

          <button
            onClick={() => navigate(ROLES_ROUTE)}
            className="p-2 hover:bg-base-200 rounded-xl transition-colors text-base-content/40 hover:text-base-content"
          >
            <X size={24} />
          </button>
        </div>

        {/* Form Area */}
        <div className="p-6 sm:p-8 max-w-[1200px]">
          <form onSubmit={handleSubmit} className="space-y-10">

            {/* Identity & Scope */}
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-8">

                {/* Name */}
                <div className="space-y-2">
                  <label htmlFor="name" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                    Name *
                  </label>

                  <input
                    id="name"
                    readOnly
                    type="text"
                    value={form.name}
                    className="w-full px-4 py-3 bg-base-200/50 border border-base-300 rounded-xl text-base-content/40 font-black uppercase cursor-not-allowed outline-none"
                  />

                  <p className="text-xs text-base-content/30 ml-1">
                    Immutable key
                  </p>
                </div>

                {/* Display Name */}
                <div className="space-y-2">
                  <label htmlFor="displayName" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                    Display Name *
                  </label>

                  <input
                    id="displayName"
                    type="text"
                    maxLength={50}
                    placeholder="e.g. Custom Manager"
                    className={`w-full px-4 py-3 bg-base-100 border rounded-xl text-base-content placeholder:text-base-content/20 focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all font-bold ${fieldErrors.displayName
                      ? 'border-error'
                      : 'border-base-300'
                      }`}
                    value={form.displayName}
                    onBlur={e =>
                      handleBlur('displayName', e.target.value)
                    }
                    onChange={e => {
                      setForm(f => ({
                        ...f,
                        displayName: e.target.value
                      }));

                      if (fieldErrors.displayName) {
                        setFieldErrors(prev => ({
                          ...prev,
                          displayName: null
                        }));
                      }
                    }}
                  />

                  {fieldErrors.displayName && (
                    <p className="text-error text-xs mt-1 font-medium">
                      {fieldErrors.displayName}
                    </p>
                  )}
                </div>

                {/* Scope */}
                <div className="space-y-2">
                  <label htmlFor="scope" className="text-[10px] font-black text-base-content/40 uppercase tracking-[0.2em] block">
                    Scope
                  </label>

                  <div className="relative" ref={scopeDropdownRef}>
                    <button
                      id="scope"
                      type="button"
                      onClick={() => setIsScopeOpen(!isScopeOpen)}
                      className="w-full px-4 py-3 bg-base-100 border border-base-300 rounded-xl text-base-content font-bold focus:ring-2 focus:ring-primary focus:border-primary outline-none transition-all cursor-pointer group flex items-center justify-between"
                    >
                      <span className="truncate flex-1 text-left">
                        {form.scope === SCOPE_PLATFORM ? 'Platform Wide' :
                          form.scope === SCOPE_ACCOUNT ? 'Account Level' :
                            'Sub-Account Level'}
                      </span>
                      <ChevronDown size={14} className={`text-base-content/20 group-hover:text-primary transition-transform ${isScopeOpen ? 'rotate-180' : ''}`} />
                    </button>

                    {isScopeOpen && (
                      <div className="absolute left-0 right-0 top-full mt-2 bg-base-100 border border-base-content/10 rounded-xl shadow-2xl z-[100] overflow-hidden animate-in fade-in slide-in-from-top-1 duration-200 flex flex-col">
                        {[
                          { val: SCOPE_PLATFORM, label: 'Platform Wide' },
                          { val: SCOPE_ACCOUNT, label: 'Account Level' },
                          { val: SCOPE_SUB_ACCOUNT, label: 'Sub-Account Level' }
                        ].filter(opt => availableScopes.includes(opt.val)).map((opt) => (
                          <button
                            key={opt.val}
                            type="button"
                            onClick={() => {
                              setForm(f => ({
                                ...f,
                                scope: opt.val,
                                permissionIds: f.permissionIds.filter(permId => {
                                  const permission = permissions.find(p => p.id === permId || p.key === permId || p._id === permId);
                                  if (!permission) { return false; }

                                  const excludedKeys = [];
                                  if (opt.val === SCOPE_ACCOUNT) {
                                    excludedKeys.push(ACCOUNTS_CREATE, ACCOUNTS_UPDATE, ACCOUNTS_DELETE);
                                  } else if (opt.val === SCOPE_SUB_ACCOUNT) {
                                    excludedKeys.push(
                                      ACCOUNTS_CREATE, ACCOUNTS_UPDATE, ACCOUNTS_DELETE,
                                      'sub_accounts.create', 'sub_accounts.update', 'sub_accounts.delete'
                                    );
                                  } else {
                                    // If switching back to platform scope, we can keep all permissions since platform includes all scopes
                                  }

                                  return !excludedKeys.includes(permission.key);
                                })
                              }));
                              setIsScopeOpen(false);
                            }}
                            className={`w-full px-4 py-3 text-left hover:bg-primary/5 transition-all flex items-center gap-2 group border-b border-base-content/5 last:border-none ${form.scope === opt.val ? 'bg-primary/5' : ''}`}
                          >
                            <span className={`text-[11px] font-black uppercase tracking-widest ${form.scope === opt.val ? 'text-primary' : 'text-base-content/40 group-hover:text-primary'}`}>
                              {opt.label}
                            </span>
                            {form.scope === opt.val && <Check size={12} className="text-primary ml-auto" />}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

              </div>
            </div>

            {/* Permissions Selection Section */}
            <PermissionGrid
              isLoading={loading}
              permissions={filteredPermissions}
              selectedIds={form.permissionIds}
              onChange={(newIds) => setForm(f => ({ ...f, permissionIds: newIds }))}
            />
            {fieldErrors.permissionIds && <p className="text-error text-xs mt-2 font-medium">{fieldErrors.permissionIds}</p>}

            {/* Actions */}
            <div className="flex gap-4 pt-12 border-t border-base-content/5">

              <button
                type="button"
                onClick={() => navigate(ROLES_ROUTE)}
                className="flex-1 py-4 border border-base-content/20 text-base-content/70 bg-base-100 rounded-xl font-black uppercase tracking-widest text-xs hover:bg-base-200 transition"
              >
                Cancel
              </button>

              <button
                type="submit"
                disabled={submitting}
                className="flex-1 btn-premium"
              > <Save size={18} />
                {submitting
                  ? 'Updating...'
                  : 'Save changes'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default EditRole;
